import React from 'react'
import { flowRight } from 'lodash'
import PropTypes from 'prop-types'
import { useTranslation } from 'react-i18next'
import { matchPath, withRouter } from 'react-router-dom'
import { useQuery } from '@apollo/client'
import gql from 'graphql-tag'
import { getPrevLocation } from 'lib/utils/store/navHistory'
import { withErrorBoundary } from 'lib/errorBoundary'
import { bosCanBeDeleted, bosDeleteRequiresReason } from 'lib/utils/store/bos'
import { fetchPolicy } from 'lib/apolloFetchPolicy'
import { getLocale } from 'lib/utils/getLocale'

import LoadingIndicator from 'components/LoadingIndicator'
import BosDeleteLogic from 'components/BillOfSale/BosDeleteLogic'
import Button from 'components/UI/Button'
import StatusMessage from 'components/StatusMessage'
import ErrorComponent from 'components/Errors/ErrorComponent'
import BosCancelForm from 'components/BillOfSale/BosCancelForm/BosCancelForm'
import { DataLoading } from 'components/Util/Loading'
import AppContext from 'components/AppContext'

const BosDelete = ({ bosId, onClose, history, createNewAfterDelete, message, submitText }) => {
    const { t, i18n } = useTranslation(['billOfSale'])

    const query = useQuery(gql`
        query bosDelete($bosId: ID) {
            bos(id: $bosId) {
                id
                status
                seller {
                    sellerSignature
                }
                parentVehicle {
                    id
                }
            }
        }
    `, { variables: { bosId }, fetchPolicy: fetchPolicy.NETWORK_ONLY })

    const renderDeleteConfirmation = (deleteLogic) => {
        const { bos } = query.data

        const {
            deleteBos,
            error: deleteError,
        } = deleteLogic

        const requireReason = bosDeleteRequiresReason(bos)

        return (
            <>
                {!!deleteError && (
                    <ErrorComponent
                        error={deleteError}
                        container={false}
                    />
                )}
                {bosCanBeDeleted(bos) ? (
                    <StatusMessage type="alert">
                        <div>
                            <div className="info">
                                <p>{requireReason ? t('billOfSale:actions.delete.infoWithBos') : message || t('billOfSale:actions.delete.info')}</p>
                            </div>
                            <BosCancelForm
                                bos={bos}
                                onSubmit={async (values) => {
                                    await deleteBos(bos.id, values.reason)
                                        .then(() => {
                                            if (createNewAfterDelete) {
                                                history.replace(`${getLocale(i18n.language)}/ajoneuvo/${bos.parentVehicle.id}/luo-sahkoinen-kauppakirja`)
                                                return
                                            }

                                            const prevLocation = getPrevLocation()
                                            if (matchPath(prevLocation.pathname, { path: `${getLocale(i18n.language)}/ajoneuvo/:vehicleId` })) {
                                                history.replace(prevLocation.pathname)
                                            } else {
                                                history.replace(`${getLocale(i18n.language) || '/'}`)
                                            }
                                        })
                                }}
                                requireReason={requireReason}
                                onAbort={onClose}
                                reasonLabel=""
                                reasonPlaceholder={t('billOfSale:actions.delete.reasonPlaceholder')}
                                submitText={submitText || t('billOfSale:actions.delete.ctaConfirm')}
                            />
                        </div>
                    </StatusMessage>
                ) : (
                    <StatusMessage type="alert">
                        <p>{t('billOfSale:actions.delete.deleteProhibited')}</p>

                        <Button
                            trackName="close"
                            className="cta"
                            onClick={onClose}
                        >
                            {t('common:close', 'Close')}
                        </Button>
                    </StatusMessage>
                )}
            </>
        )
    }

    return (
        <AppContext context="bosDelete">
            <div className="bos-delete">
                <DataLoading data={query}>
                    {() => (
                        <BosDeleteLogic>
                            {(deleteLogic) => (
                                deleteLogic.loading ? (
                                    <LoadingIndicator />
                                ) : (
                                    renderDeleteConfirmation(deleteLogic)
                                )
                            )}
                        </BosDeleteLogic>
                    )}
                </DataLoading>
            </div>
        </AppContext>
    )
}

BosDelete.propTypes = {
    bosId: PropTypes.string.isRequired,
    onClose: PropTypes.func.isRequired,
    createNewAfterDelete: PropTypes.bool,
    message: PropTypes.string,
    submitText: PropTypes.string,
}

export default flowRight(
    withErrorBoundary,
    withRouter,
)(BosDelete)
